import _ from "lodash";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";
import { getSettingValue } from "modules/PermissionModule";
import { getCloudUrl } from "Constants";
import {
  parseConfigurationValue,
  getArchivedSortedSubjectListMemoized,
  generateNestedSelectedData,
  getPYPElementSetFromNodes,
} from "Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getCurriculumProgramIdFromCurrentAdminNavigationNode,
  getAcademicYearForCurriculumProgram,
  getCurrentAcademicYear,
  getUserPrimaryGradeId,
} from "modules/CommonSelectors";
import { LEARNING_COURSE_PERM } from "Constants/permissionConstants";
import { readConfigurationFragmentV2 } from "ChildrenPage/modules/ChildrenPageGraphqlHelpers";
import { getUnitFlowUtils } from "UnitFlowV3/modules/utils";
import {
  generateFieldListFromTemplateFields,
  getLearningCourseSubjects,
  getUniqSubjectsFromNodes,
  mergeSubjectsWithNodes,
} from "UnitPlans/modules/Utils";
import { getIsLearningCoursesEnabledForUnit } from "UnitPlans/modules/UnitViewModeUtils";
import {
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  FIELD_TYPE_RICH_TEXT,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  UBD_SUBJECT_SPECIFIC,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";
import { getLearningCourseTermDates } from "UnitPlans/modules/LearningCourseFlow/Utils";
import moment from "moment";
import {
  getIsAYWisePermsSetupEnabled,
  getIsModuleSettingsV2Enabled,
} from "Utils/ACLStoreUtils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  getSubjectGroupsFromCache,
  getUniquelyCombinedGradesDetails,
} from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import { getLearningCourseVariables } from "Teacher/routes/LearningCourse/modules/Utils";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getPropertyValue } from "Fields/FieldUtils";
import {
  getIsCourseBasedGradingEnabled,
  getIsSubjectBasedGradingEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";

export const getSubjectOptions = ({ subjects }) => {
  return _.map(subjects, (subject, index) => {
    return {
      node: subject,
      label: subject.name,
      value: subject.id,
      benchmarkRootNode: subject.benchmarkRootNode,
      isArchived: _.get(subject, "isArchived", false),
    };
  });
};
export const getSubjectList = ({
  grades,
  readOnly,
  unitPlanFields,
  learningCourseId,
  academicYearId,
}) => {
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree",
    ""
  );

  let subjects = [];
  if (!readOnly) {
    //For MYP Fetch Subjects from Course
    //uniquely combined subjects from gradeDetailsQuery
    subjects = _.get(getUniquelyCombinedGradesDetails(grades), "subjects", []);
  }
  if (!_.isEmpty(learningCourseId)) {
    const learningCourseSubjects = getLearningCourseSubjects({
      academicYearId,
      learningCourseId,
    });
    const filteredSubjects = _.intersectionBy(
      subjects,
      learningCourseSubjects,
      "id"
    );
    return _.uniqBy(
      [...filteredSubjects, ..._.get(resolvedValue, "subjects", [])],
      "id"
    );
  }

  return _.uniqBy([...subjects, ..._.get(resolvedValue, "subjects", [])], "id");
};
export const getStartEndDatesOfAcademicYears = ({ academicYears }) => {
  const sortedAcademicYears = _.sortBy(academicYears, [item => item.startDate]);
  return {
    startDate: _.get(_.first(sortedAcademicYears), "startDate", null),
    endDate: _.get(_.last(sortedAcademicYears), "endDate", null),
  };
};

export const getSubjectIdsFromUnitPlanDetails = ({ unitPlanDetails }) => {
  const subjects = _.get(
    unitPlanDetails,
    "subjects.resolvedMinimalTree.subjects"
  );
  return _.map(subjects, "id");
};

export const cleanNewlines = str => {
  return str.replace(/\r?\n\s*/g, " ").trim();
};

export const getSubjectRecommendedPhaseMap = ({ subjects }) => {
  return _.reduce(
    subjects,
    (result, subject) => {
      const subjectId = subject?.id;
      const recommendedPhases = _.get(subject, "recommendedPhase", []);

      result[subjectId] = recommendedPhases;
      return result;
    },
    {}
  );
};

const parseAdminMenuId = id => {
  if (!id) return {};

  if (_.includes(id, ":")) {
    const [curriculumProgramId, nodeId] = _.split(id, ":");
    return { curriculumProgramId, nodeId };
  } else return { nodeId: id };
};

// Extract curriculumProgramId from admin portal routes
const getCurriculumProgramIdFromRoute = state => {
  let _curriculumProgramId = null;

  const loginActiveTab = _.get(state, "login.activeTab");
  const isAdminPortal = loginActiveTab === "admin";

  const isAdminNavigationRevampPortal = _.includes(
    ["accountSettingsTab", "schoolSettingsTab"],
    loginActiveTab
  );

  if (isAdminPortal) {
    const { subChildId } = _.get(state, "administrator.routes", {});
    const { curriculumProgramId } = parseAdminMenuId(subChildId);

    _curriculumProgramId = curriculumProgramId;
  }

  if (isAdminNavigationRevampPortal) {
    _curriculumProgramId = getCurriculumProgramIdFromCurrentAdminNavigationNode(
      state
    );
  }

  return _curriculumProgramId;
};

export const getIsLearningCoursesEnabled = args => {
  const state = store?.getState();
  let isEnabled = false;
  const isAYWisePermsSetupEnabled = getIsAYWisePermsSetupEnabled();
  const userInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = userInfo.id;
  const userType = _.get(userInfo, "user_type", "");
  const gradeId = getUserPrimaryGradeId();

  const isModuleSettingsV2Enabled = getIsModuleSettingsV2Enabled();

  let curriculumProgramId = _.get(
    args,
    "curriculumProgramId",
    _.get(state, "platform.currentCurriculumProgram.id", undefined)
  );

  if (_.isEmpty(curriculumProgramId)) {
    // try extracting curriculumProgramId from admin portal routes
    curriculumProgramId = getCurriculumProgramIdFromRoute(state);
  }

  let academicYearId = _.get(args, "academicYearId", null);

  if (!academicYearId && curriculumProgramId) {
    // If academicYearId is not provided, try to get it from the curriculum program
    academicYearId = getAcademicYearForCurriculumProgram(state, {
      curriculumProgramId,
    })?.id;
  }

  if (curriculumProgramId && academicYearId) {
    const configurationEntityGroups = [
      { entityType: "CURRICULUM_PROGRAM", entityId: curriculumProgramId },
    ];

    if (isAYWisePermsSetupEnabled) {
      configurationEntityGroups.push({
        entityType: "ACADEMIC_YEAR",
        entityId: academicYearId,
      });
    } else {
      if (userType !== "staff" && isModuleSettingsV2Enabled) {
        configurationEntityGroups.push({
          entityType: "GRADE",
          entityId: gradeId,
        });
      }

      configurationEntityGroups.push({ entityType: "USER", entityId: userId });
    }

    const configurationVariables = {
      configurationEntityGroupFilters: [
        {
          configurationEntityGroups,
        },
      ],
    };

    const settingValue = readConfigurationFragmentV2(
      configurationVariables,
      LEARNING_COURSE_PERM
    );

    isEnabled = parseConfigurationValue(settingValue);
  }

  return isEnabled;
};

export const getPromptsVisibleType = () => {
  const userInfo = getUserInfo();
  const userId = userInfo.id;
  const existingPromptsVisibility = getSettingValue({
    name: "UnitPromptConfiguration",
    userId,
    readFromUserAndOrgLevel: true,
  });
  return existingPromptsVisibility;
};

// used to generate reset options for a field
// example case like if we delete all the cards then we need to reset the options for the field
export const getResetOptions = ({ fieldObj, plannerElementNodeSets }) => {
  const fieldType = _.get(fieldObj, "type", "");
  switch (fieldType) {
    case "GenericCardList": {
      const config = _.get(fieldObj, "config", {});
      const isFieldWithDeleteAction = _.get(
        config,
        "fieldTypeConfig.isUpdateActionsAllowed",
        false
      );
      if (isFieldWithDeleteAction) {
        const plannerElementConfigType = _.get(
          config,
          "plannerElementConfig.type",
          ""
        );

        const plannerElementNodeSet =
          _.find(plannerElementNodeSets, {
            type: plannerElementConfigType,
          }) || {};

        const nodes = _.map(
          _.get(
            plannerElementNodeSet,
            "plannerElementNodeSetWrapperV2.publishedData.edges",
            []
          ),
          "node"
        );
        return nodes;
      }

      return [];
    }
    default:
      return [];
  }
};

export const getResetOptionsForV2 = ({ fieldObj, plannerElementNodeSets }) => {
  const uiType = getPropertyValue({
    fieldObj,
    propertyPath: "uiType",
  });
  switch (uiType) {
    case "GenericCardList": {
      const isFieldWithDeleteAction = getPropertyValue({
        fieldObj,
        propertyPath: "fieldTypeConfigIsUpdateActionsAllowed",
      });
      if (isFieldWithDeleteAction) {
        const plannerElementConfigType = getPropertyValue({
          fieldObj,
          propertyPath: "plannerElementConfigType",
        });
        const plannerElementNodeSet =
          _.find(plannerElementNodeSets, {
            type: plannerElementConfigType,
          }) || {};
        return _.map(
          _.get(
            plannerElementNodeSet,
            "plannerElementNodeSetWrapperV2.publishedData.edges",
            []
          ),
          "node"
        );
      }
      return [];
    }
    default:
      return [];
  }
};

export const showNewUnitFlow = () => {
  const isSinglePageEnabled = ACLStore.can(
    "FeatureFlag:EnableSinglePageUnitPlan"
  );
  const _isLearningCoursesEnabled = getIsLearningCoursesEnabledForUnit();
  if (_isLearningCoursesEnabled) {
    return true;
  }
  if (isSinglePageEnabled) {
    return false;
  } else {
    const { isUnitFlowRevampEnabled } = getUnitFlowUtils();
    return isUnitFlowRevampEnabled();
  }
};

export const getIconUrlFromKey = ({ iconKey }) => {
  return `${getCloudUrl()}/assets/${iconKey}.svg`;
};

export const getStructuredData = ({ template, module_id }) => {
  const templateBody = _.get(template, "body", {});
  const stages = _.get(templateBody, `module_list.${module_id}.stages`, []);
  const stage_list = _.get(templateBody, "stage_list", {});
  const step_list = _.get(templateBody, "step_list", {});
  const field_list = _.get(templateBody, "field_list", {});
  const fieldListV2 = _.get(templateBody, ["fieldListV2"], {});

  return _.filter(
    _.map(stages, stageId => {
      const stage = stage_list[stageId];
      if (!stage) return null;

      const steps = _.filter(
        _.map(stage.steps, stepId => {
          const step = step_list[stepId];
          if (!step) return null;

          const fields = _.filter(
            _.map(step.fields, fieldId => {
              const field = field_list[fieldId] || fieldListV2[fieldId];
              return field ? { ...field, id: fieldId } : null;
            })
          );

          return { id: stepId, ...step, fields };
        })
      );

      return { id: stageId, ...stage, steps };
    })
  );
};

const getUpdatedFieldTypeConfig = ({
  fieldTypeConfig,
  hasSubtext,
  mode,
  fieldKey,
  t,
}) => {
  if (fieldKey === "globalContextMYP") {
    return {
      ...fieldTypeConfig,
      emptyText: t("common:not_selected"),
    };
  }
  if (!hasSubtext && mode === "edit") {
    return {
      ...fieldTypeConfig,
      containerStyle: {
        gap: "12px",
        // Writing following thing afterwards to avoid overriding of containerStyle
        ..._.get(fieldTypeConfig, "containerStyle", {}),
      },
    };
  } else {
    return fieldTypeConfig;
  }
};

export const getUpdatedFieldTypeConfigMemoized = _.memoize(
  params => getUpdatedFieldTypeConfig(params),
  // Applied following line as there are many dependencies of fieldTypeConfig -> like this can be different for different unit plan template
  // so we can't break fieldTypeConfig into multiple parts for memoization
  // eslint-disable-next-line
  params => JSON.stringify(params)
);

const getUpdatedFieldTypeConfigForV2 = ({
  configs,
  hasSubtext,
  mode,
  showFilterOnResponses,
}) => {
  const fieldTypeConfig = { showFilterOnResponses };

  _.forEach(configs, value => {
    const configKey = _.get(value, "config", "");
    const configValue = _.get(value, "value", "");

    if (configKey === "selectionType") {
      if (configValue === "MULTI_SELECT")
        fieldTypeConfig["allowMultiSelect"] = true;
      else {
        fieldTypeConfig["allowMultiSelect"] = false;
        fieldTypeConfig["updateSingleSelectValueAsList"] = true;
      }
    }
  });

  return getUpdatedFieldTypeConfig({
    fieldTypeConfig,
    hasSubtext,
    mode,
  });
};

export const getUpdatedFieldTypeConfigForV2Memoized = _.memoize(
  params => getUpdatedFieldTypeConfigForV2(params),
  // Applied following line as there are many dependencies of fieldTypeConfig -> like this can be different for different unit plan template
  // so we can't break fieldTypeConfig into multiple parts for memoization
  // eslint-disable-next-line
  params => JSON.stringify(params)
);

export const getIsStandardsField = ({ plannerElementType }) =>
  _.includes(
    [
      ELEMENT_TYPE_MYP_LEARNING_STANDARD,
      ELEMENT_TYPE_DP_LEARNING_STANDARD,
      ELEMENT_TYPE_UBD_LEARNING_STANDARD,
      ELEMENT_TYPE_PYP_LEARNING_STANDARD,
    ],
    plannerElementType
  );

export const getIsObjectivesField = ({ plannerElementType }) =>
  _.includes([ELEMENT_TYPE_MYP_OBJECTIVES], plannerElementType);

export const getFieldListV2 = ({ templateId, data }) => {
  const template = _.find(data, { id: templateId });

  if (!template) {
    return {};
  }

  const templateFields = _.get(template, ["templateField", "edges"]);
  return generateFieldListFromTemplateFields({
    templateFields,
    templateId,
  });
};

// utility function to get field type specific empty text
export const getEmptyTextByFieldType = ({
  type,
  t,
  defaultEmptyText = "",
  hasSelection = false,
}) => {
  if (!_.isEmpty(defaultEmptyText)) {
    return defaultEmptyText;
  }

  switch (type) {
    case "GenericCheckList":
    case "GenericCardList":
    case "CARD":
      return t("common:not_selected");
    case "InputNumberList":
      if (hasSelection) {
        return t("common:not_selected");
      }
      return t("common:not_defined");
    case FIELD_TYPE_RICH_TEXT:
    case "TextView":
    case "InputField":
      return t("common:not_defined");
    default:
      return "";
  }
};

export const isUnitFlowPrintEnabled = () => {
  return ACLStore.can("FeatureFlag:UnitFlowPrint");
};

export const getOptionsForUnitPlanDetailsField = ({
  fieldKey,
  noMinMaxDates = false,
  academicYears,
  learningCourseId,
  academicYearId,
  selectedAcademicYear,
  organizationId,
  curriculumType,
  unitPlanFields,
  courseId,
  mode,
  curriculumId,
  objectiveLevelNodes = [],
  grades,
  readOnly,
  unitPlanCoursesSubjects = [],
  unitType: unitType_,
}) => {
  const unitTypeFromUnitPlanFields = _.get(
    _.find(unitPlanFields, { uid: "unitType" }),
    "value",
    ""
  );
  const unitType = unitType_ || unitTypeFromUnitPlanFields;
  switch (fieldKey) {
    case "dateDuration": {
      let academicYearStartDate, academicYearEndDate, minDate, maxDate;

      if (noMinMaxDates) {
        return;
      }

      const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
      if (isLearningCoursesEnabled) {
        const { startDate, endDate } = getLearningCourseTermDates({
          learningCourseId,
          academicYearId,
        });
        minDate = moment(startDate).format("YYYY-MM-DD");
        maxDate = moment(endDate).format("YYYY-MM-DD");
        return {
          minDate,
          maxDate,
        };
      }
      if (!_.isEmpty(academicYears)) {
        const { startDate, endDate } = getStartEndDatesOfAcademicYears({
          academicYears,
        });

        minDate = moment(startDate).format("YYYY-MM-DD");
        maxDate = moment(endDate).format("YYYY-MM-DD");
      } else {
        if (!_.isNil(selectedAcademicYear)) {
          academicYearStartDate = selectedAcademicYear.startDate;
          academicYearEndDate = selectedAcademicYear.endDate;
        } else {
          const currentAcademicYear = getCurrentAcademicYear({
            organizationId,
          });
          academicYearStartDate = _.get(currentAcademicYear, "startDate");
          academicYearEndDate = _.get(currentAcademicYear, "endDate");
        }

        minDate = moment(academicYearStartDate).format("YYYY-MM-DD");
        maxDate = moment(academicYearEndDate).format("YYYY-MM-DD");
      }

      return {
        minDate,
        maxDate,
      };
    }
    case "subjects": {
      let subjects = [];
      let subjectGroups = [];
      let subjectList = [];
      let courseDetails = {};
      const subjectListFromUnitPlan = _.get(
        _.find(unitPlanFields, {
          uid: "subjects",
        }),
        "resolvedMinimalTree.subjects",
        []
      );

      if (
        _.includes([CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_DP], curriculumType)
      ) {
        if (courseId) {
          const courseGradeFilters = {
            academicYearId,
          };
          courseDetails = getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          });
          subjectList = _.get(courseDetails, "subjects", []);
        } else {
          subjectList = subjectListFromUnitPlan;
        }
      }
      if (curriculumType == CURRICULUM_TYPE_DP) {
        if (mode === "edit") {
          subjectList = subjectListFromUnitPlan;
        }
        getArchivedSortedSubjectListMemoized({ subjectList });

        return subjectList;
      } else if (curriculumType == CURRICULUM_TYPE_MYP) {
        const curriculumIdAsFilter = {
          curriculumProgramIds: curriculumId,
        };

        if (learningCourseId) {
          const learningCourseVariables = getLearningCourseVariables({
            learningCourseId,
            academicYearId,
          });

          courseDetails = getLearningCourseDetailsFromCache(
            learningCourseVariables
          );
        }

        const subjectGroupData = _.get(
          getSubjectGroupsFromCache({
            organizationId: organizationId,
            filters: curriculumIdAsFilter,
          }),
          "subjectGroups",
          []
        );

        subjectGroups = _.map(subjectGroupData, group => {
          return {
            ...group,
            subjects: getArchivedSortedSubjectListMemoized({
              subjectList: _.get(group, "subjects", []),
            }),
          };
        });

        // Extract subjects from current class
        const initSubject = !_.isEmpty(subjectList) ? _.first(subjectList) : {};

        let objectiveLevelsData, selectedObjectiveLevels;
        const selectedObjectiveLevelsWithValue = [];

        const isObjectivePhasesEnabled = !ACLStore.can(
          "FeatureFlag:RemoveMYPObjectivesPhase"
        );

        if (isObjectivePhasesEnabled) {
          objectiveLevelsData =
            _.find(unitPlanFields, { uid: "objectiveLevel" }) || {};
          selectedObjectiveLevels = _.get(objectiveLevelsData, "value", "");

          const objectiveUidsWithSelectedValue = _.filter(
            unitPlanFields,
            field => {
              return (
                (_.startsWith(
                  _.get(field, "uid"),
                  "disciplinaryObjectivesMYP"
                ) ||
                  _.isEqual(_.get(field, "uid"), "objectivesMYP")) &&
                !_.isEmpty(field.value)
              );
            }
          );

          _.forEach(objectiveUidsWithSelectedValue, objective => {
            //for every subjects objective filtered out there selected objectiveids
            selectedObjectiveLevelsWithValue.push(
              ..._.map(
                //filtered out the selected objectives parent
                _.filter(
                  objective.resolvedMinimalTree.nodes,
                  node => node.depth == 1
                ),
                objectiveNode => objectiveNode.id
              )
            );
          });
        }

        return {
          courseDetails,
          subjectGroups,
          initSubject,
          isEditable: mode == "create",
          unitType,
          ...(isObjectivePhasesEnabled
            ? {
                objectiveLevelNodes,
                selectedObjectiveLevels,
                selectedObjectiveLevelsWithValue,
              }
            : {}),
        };
      } else {
        subjects = getSubjectList({
          grades,
          unitPlanFields,
          readOnly,
          learningCourseId,
          academicYearId,
        });

        if (
          curriculumType == CURRICULUM_TYPE_UBD &&
          unitType == UBD_SUBJECT_SPECIFIC
        ) {
          // filtering data of subjects with course mapped subjects
          if (!_.isEmpty(unitPlanCoursesSubjects)) {
            subjects = _.filter(subjects, subject =>
              _.find(
                unitPlanCoursesSubjects,
                courseSubject => courseSubject.id == subject.id
              )
            );
          }
        }
        if (curriculumType === CURRICULUM_TYPE_PYP && mode === "create") {
          // filter the archived subjects and only show the active subjects
          subjects = _.filter(subjects, subject => !subject.isArchived);
        }
        const sortedSubjects = getArchivedSortedSubjectListMemoized({
          subjectList: subjects,
        });

        return getSubjectOptions({ subjects: sortedSubjects });
      }
    }
    case "subjectLevel": {
      let subjectVariant = [];
      const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
      if (isLearningCoursesEnabled && curriculumType !== CURRICULUM_TYPE_DP) {
        const isSubjectBasedGradingEnabled = getIsSubjectBasedGradingEnabled();
        const isCourseBasedGradingEnabled = getIsCourseBasedGradingEnabled();
        if (isCourseBasedGradingEnabled) {
          const learningCourseVariables = getLearningCourseVariables({
            learningCourseId,
            academicYearId,
          });

          const learningCourseData = getLearningCourseDetailsFromCache(
            learningCourseVariables
          );
          const associatedLevels = _.get(
            learningCourseData,
            "associatedLevels",
            []
          );
          const associatedLevelsFormatted = _.map(associatedLevels, level => ({
            value: _.get(level, "id"),
            label: _.get(level, "abbreviation"),
            isArchived: _.get(level, "isArchived"),
          }));
          return associatedLevelsFormatted;
        } else if (isSubjectBasedGradingEnabled) {
          const subjects = _.get(
            _.find(unitPlanFields, { uid: "subjects" }),
            "resolvedMinimalTree.subjects",
            []
          );
          if (!_.isEmpty(subjects)) {
            const allSelectedSubjectsUniqueAssociatedLevels = _.uniqBy(
              _.flatten(_.map(subjects, s => _.get(s, "associatedLevels", []))),
              "id"
            );
            return _.map(allSelectedSubjectsUniqueAssociatedLevels, level => ({
              value: _.get(level, "id"),
              label: _.get(level, "label"),
              isArchived: _.get(level, "isArchived"),
            }));
          }
          return [];
        }
      }
      if (
        !_.isEmpty(
          _.get(
            _.find(unitPlanFields, { uid: "subjects" }),
            "resolvedMinimalTree.subjects[0].flatVariants",
            []
          )
        )
      )
        subjectVariant = _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects[0].flatVariants",
          []
        );
      //In DP for subject level there will always 1 subject mapped to course so we are getting the data of 1st subject
      else {
        const academicYearId = _.get(selectedAcademicYear, "id", "");
        const courseGradeFilters = {
          academicYearId,
        };
        subjectVariant = _.get(
          getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          }),
          "subjects[0].flatVariants",
          []
        );
      }

      const uniqueSubjectLevels = _.uniqBy(subjectVariant, function (e) {
        return e.level.id;
      });
      const subjectLevels = _.map(uniqueSubjectLevels, variant => {
        return {
          value: _.get(variant, "level.id", ""),
          label: _.get(variant, "level.label", ""),
          isArchived: _.get(variant, "isArchived", false),
        };
      });
      return subjectLevels;
    }
    default:
      return {};
  }
};

export const checkEmptyElementValueForConvertedFields = ({
  fieldKey,
  data,
  unitPlanFields,
  fieldObj,
  ignoreUnitPlanSubjectCheck = false,
  deriveSubjectIdsFromNodes = false,
  skipMergeStandardsAssociatedParents = false,
}) => {
  const allFields = _.unionBy(
    _.get(data, "allFields", []),
    _.get(data, "fields", []),
    "id"
  );
  const dataTofind = _.find(allFields, value => {
    return _.isEqual(_.get(value, "uid"), fieldKey);
  });
  const valueKeys = _.get(dataTofind, "value", "");
  const resolvedValue = _.get(dataTofind, "resolvedMinimalTree", "");
  const subjects = _.find(unitPlanFields, { uid: "subjects" });
  const subjectIds = _.get(subjects, "value", []);

  let result;

  switch (fieldKey) {
    case "relatedConcepts": {
      const subjectsToFind = _.get(data, "subjects.value", []);
      result =
        _.get(valueKeys, "length", 0) != 0 &&
        _.get(subjectsToFind, "length", 0) != 0;
      break;
    }
    case "benchmarks": {
      const benchmarksD0 = _.filter(
        _.get(resolvedValue, "benchmarks", []),
        benchmark => {
          return benchmark.depth == 0;
        }
      );

      if (ignoreUnitPlanSubjectCheck) {
        result = !_.isEmpty(benchmarksD0);
        break;
      }

      const subjects = _.find(unitPlanFields, { uid: "subjects" });

      const subjectValues = _.map(
        _.get(subjects, "resolvedMinimalTree.subjects", []),
        subject => {
          return { ...subject.benchmarkRootNode };
        }
      );

      return !_.isEmpty(_.intersectionBy(benchmarksD0, subjectValues, "id"));
    }
    case "atls": {
      result = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "atls", []),
      });
      break;
    }
    case "syllabusContentDP": {
      result = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });
      break;
    }
    case "atlDP": {
      result = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });
      break;
    }
    case "subjectStandardDP": {
      result = getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });
      break;
    }
    default: {
      const plannerElementSetId = getPropertyValue({
        fieldObj,
        propertyPath: "plannerElementSetId",
      });

      const plannerElementConfig =
        getPropertyValue({
          fieldObj,
          propertyPath: "plannerElementObj",
        }) || {};
      const plannerElementConfigType = _.get(plannerElementConfig, "type", "");

      if (
        plannerElementSetId &&
        !_.includes(
          [
            "MYP_EXPLORATION",
            "DP_PEDAGOGICAL_APPROACH",
            "DP_DIFFERENTIATION_APPROACH",
          ],
          plannerElementConfigType
        )
      ) {
        result = plannerElementValue({
          fieldObj,
          subjectIds,
          resolvedValue,
          value: valueKeys,
          deriveSubjectIdsFromNodes,
          skipRemoveSubjectCheck: ignoreUnitPlanSubjectCheck,
          skipMergeStandardsAssociatedParents,
          plannerElementConfigType,
        });
      } else {
        result = valueKeys;
      }
    }
  }

  return !_.isEmpty(result);
};

const getBenchMarkValue = ({
  values,
  benchmarkSet,
  nodes,
  shouldGenerateParentChildRelation,
  startDepth,
}) => {
  return benchmarkSet
    ? generateNestedSelectedData({
        nodes: _.get(benchmarkSet, "nodes", []),
        rootNodes: _.get(benchmarkSet, "rootNodes", []),
        value: values,
        shouldGenerateParentChildRelation,
      })
    : generateNestedSelectedData({
        ...getPYPElementSetFromNodes({ nodes, startDepth }),
        value: values,
        shouldGenerateParentChildRelation,
      });
};

const getBenchMarkValueMemoized = _.memoize(getBenchMarkValue, params =>
  JSON.stringify(params)
);

const plannerElementValue = ({
  fieldObj,
  subjectIds,
  resolvedValue,
  value,
  deriveSubjectIdsFromNodes,
  skipRemoveSubjectCheck,
  skipMergeStandardsAssociatedParents,
  plannerElementConfigType,
}) => {
  const nodes = _.get(resolvedValue, "nodes", []);
  const dynamicSubject = getPropertyValue({
    fieldObj,
    propertyPath: "dynamicSubject",
  });
  const uiType = getPropertyValue({
    fieldObj,
    propertyPath: "uiType",
  });

  if (uiType === "PlannerElementDataSet") {
    let subjectNodes = getUniqSubjectsFromNodes({ nodes });
    let updatedNodes = nodes;

    if (_.isEmpty(subjectIds) && deriveSubjectIdsFromNodes) {
      subjectIds = _.map(subjectNodes, item => item.id);
    }

    // Following condition is given to remove the subjects which are selected but removed afterwards
    if (
      _.includes(
        ["MYP_LEARNING_STANDARD", "UBD_LEARNING_STANDARD"],
        plannerElementConfigType
      ) &&
      !skipRemoveSubjectCheck
    ) {
      const subjects = _.map(subjectIds, subjectId => {
        return { id: subjectId };
      });

      const updatedValues = _.filter(updatedNodes, updatedNode => {
        const nodeSubjects = _.get(
          updatedNode,
          "dataSetNodeWrapper.properties.subjects",
          []
        );
        return _.intersectionBy(subjects, nodeSubjects, "id").length > 0;
      });

      updatedNodes = updatedValues;
    }

    if (
      _.includes(
        [
          "MYP_RELATED_CONCEPT",
          "MYP_SPECIFIED_CONCEPTS",
          "MYP_LEARNING_STANDARD",
          "MYP_ATL",
          "UBD_LEARNING_STANDARD",
          "UBD_ATL",
          "DP_ATL",
        ],
        plannerElementConfigType
      )
    ) {
      let filteredSubjectIds = subjectIds;

      if (!_.isEmpty(dynamicSubject)) {
        filteredSubjectIds = [dynamicSubject.id];
      }

      if (!_.isEmpty(filteredSubjectIds)) {
        subjectNodes = _.filter(subjectNodes, node =>
          _.includes(filteredSubjectIds, node.id)
        );
      }
    }

    if (
      _.size(subjectNodes) > 1 &&
      !_.isEqual(plannerElementConfigType, "UBD_LEARNING_STANDARD") &&
      !skipMergeStandardsAssociatedParents
    ) {
      updatedNodes = mergeSubjectsWithNodes({
        subjectNodes,
        nodes: updatedNodes,
      });
    }

    return generateNestedSelectedData({
      ...getPYPElementSetFromNodes({ nodes: updatedNodes }),
      value,
    });
  }
};
